import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toStableCapturifyUrl } from '../lib/capturifyImages';
import { logger } from '../lib/loggingService';

/**
 * The landing page for a project handed over from Capturify.
 *
 * Capturify's "Generate report" button links here with ?capturifyProject=<id>.
 * The server fetches that project with an API key (no public share link is ever
 * created), and this page shows every photo grouped by the date it was taken so
 * a technician can pick which visits belong in the report and start it.
 *
 * The paste-a-link importer (DataScraper) is untouched and still the fallback:
 * if this fails, the button below drops straight into it.
 */

interface ImageItem {
  id: string;
  url: string;
  alt?: string;
}

interface CapturifyProjectProps {
  clientId: string;
  /** Short-lived handoff token from Capturify; without it the server serves nothing. */
  handoffToken: string;
  onDataExtracted: (data: {
    clientName: string;
    clientAddress: string;
    chimneyType: string;
    reportDate: string;
    timelineCoverImage: string;
    scrapedImages: ImageItem[];
    dataSourceUrl: string;
  }) => void;
  /** Drops back to the paste-a-link importer. */
  onUseManualImport: () => void;
  userEmail?: string;
}

interface ApiImage {
  id: number | string;
  inspectionDate?: string;
  createdAt?: string;
  rotation?: number;
  imageUrl: string;
}

interface ApiProject {
  clientId?: number;
  clientName?: string;
  clientAddress?: string;
  coverImageUrl?: string | null;
  images?: ApiImage[];
  count?: number;
}

interface DateGroup {
  date: string;
  images: { id: string; url: string }[];
}

const CAPTURIFY_APP_BASE = (
  process.env.REACT_APP_CAPTURIFY_URL || 'https://capturify.chimneysweeps.com'
).replace(/\/$/, '');

/** "2026-10-03" -> "Saturday, 3 October 2026". Falls back to the raw value. */
function formatDate(iso: string): string {
  if (!iso || iso === 'undated') return 'Undated';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

const CapturifyProject: React.FC<CapturifyProjectProps> = ({
  clientId, handoffToken, onDataExtracted, onUseManualImport, userEmail = 'unknown',
}) => {
  const [project, setProject] = useState<ApiProject | null>(null);
  const [groups, setGroups] = useState<DateGroup[]>([]);
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `/api/capturify/project/${encodeURIComponent(clientId)}?t=${encodeURIComponent(handoffToken)}`
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data) {
        throw new Error(data?.message || `Couldn't load that project (status ${res.status})`);
      }

      // Group by the date the photo was taken, newest visit first.
      const byDate = new Map<string, { id: string; url: string }[]>();
      (data.images || []).forEach((img: ApiImage) => {
        if (!img.imageUrl) return;
        const date = img.inspectionDate || (img.createdAt || '').split('T')[0] || 'undated';
        if (!byDate.has(date)) byDate.set(date, []);
        // Presigned S3 links expire in an hour and reports store URLs verbatim,
        // so convert to Capturify's stable redirect before anything keeps them.
        byDate.get(date)!.push({ id: String(img.id), url: toStableCapturifyUrl(img.imageUrl) });
      });

      const ordered: DateGroup[] = Array.from(byDate.entries())
        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
        .map(([date, images]) => ({ date, images }));

      setProject(data);
      setGroups(ordered);
      setSelectedDates(new Set(ordered.map((g) => g.date)));  // everything, by default

      await logger.logExtractionSuccess(userEmail, data.clientName || `capturify_${clientId}`, {
        client_name: data.clientName,
        images_count: (data.images || []).length,
        has_address: !!data.clientAddress,
      });
    } catch (err: any) {
      setError(err?.message || 'Could not load that Capturify project.');
    } finally {
      setLoading(false);
    }
  }, [clientId, handoffToken, userEmail]);

  useEffect(() => { load(); }, [load]);

  const toggleDate = (date: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const selectedImages = useMemo(
    () => groups.filter((g) => selectedDates.has(g.date)).flatMap((g) => g.images),
    [groups, selectedDates]
  );

  const totalImages = useMemo(
    () => groups.reduce((n, g) => n + g.images.length, 0),
    [groups]
  );

  const handleStart = () => {
    if (!project || selectedImages.length === 0) return;
    const cover = project.coverImageUrl
      ? toStableCapturifyUrl(project.coverImageUrl)
      : selectedImages[0].url;

    onDataExtracted({
      clientName: project.clientName || '',
      clientAddress: project.clientAddress || '',
      chimneyType: 'masonry',
      reportDate: new Date().toISOString().split('T')[0],
      timelineCoverImage: cover,
      scrapedImages: selectedImages.map((img, i) => ({
        id: img.id,
        url: img.url,
        alt: `Capturify photo ${i + 1}`,
      })),
      dataSourceUrl: `${CAPTURIFY_APP_BASE}/projects/client/${clientId}`,
    });
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-sm text-gray-500">
        Loading this project from Capturify&hellip;
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 sm:p-8">
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
        </div>
        <div className="mt-4 flex flex-wrap gap-2.5">
          <button
            type="button"
            onClick={load}
            className="px-4 py-2.5 bg-[#722420] text-white rounded-lg hover:bg-[#8B2E26] text-sm font-medium"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={onUseManualImport}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Import from a link instead
          </button>
          <a
            href={`${CAPTURIFY_APP_BASE}/projects/client/${clientId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Open in Capturify
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      {/* Who this report is for */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">From Capturify</p>
        <h2 className="mt-1 text-2xl font-bold text-gray-900">
          {project?.clientName || 'Unnamed project'}
        </h2>
        {project?.clientAddress && (
          <p className="mt-1 text-sm text-gray-600">{project.clientAddress}</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
          <span>{totalImages} photo{totalImages !== 1 ? 's' : ''}</span>
          <span>{groups.length} visit{groups.length !== 1 ? 's' : ''}</span>
          <a
            href={`${CAPTURIFY_APP_BASE}/projects/client/${clientId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#722420] hover:underline underline-offset-4"
          >
            Open this project in Capturify
          </a>
        </div>
      </div>

      {/* Photos, grouped by the day they were taken */}
      {groups.map((group) => {
        const on = selectedDates.has(group.date);
        return (
          <div
            key={group.date}
            className={`bg-white border rounded-xl shadow-sm overflow-hidden ${
              on ? 'border-[#722420]/40' : 'border-gray-200'
            }`}
          >
            <label className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 cursor-pointer">
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleDate(group.date)}
                className="h-4 w-4 rounded border-gray-300 text-[#722420] focus:ring-[#722420]"
              />
              <span className="font-semibold text-gray-900">{formatDate(group.date)}</span>
              <span className="text-sm text-gray-500">
                {group.images.length} photo{group.images.length !== 1 ? 's' : ''}
              </span>
            </label>

            <div className={`grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 p-4 ${on ? '' : 'opacity-40'}`}>
              {group.images.map((img) => (
                // Portrait tiles: these come off phones, so 3:4 fills the tile where a
                // square would letterbox. object-contain, never cover - a cropped
                // thumbnail hides the defect the photo was taken to show.
                <div key={img.id} className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={img.url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Start */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
        <button
          type="button"
          onClick={handleStart}
          disabled={selectedImages.length === 0}
          className="inline-flex items-center justify-center px-5 py-2.5 bg-[#722420] text-white rounded-lg hover:bg-[#8B2E26] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
        >
          Start report with {selectedImages.length} photo{selectedImages.length !== 1 ? 's' : ''}
        </button>
        <button
          type="button"
          onClick={onUseManualImport}
          className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-4"
        >
          Import from a link instead
        </button>
      </div>
    </div>
  );
};

export default CapturifyProject;
